import _ from 'lodash';
import type { GetServerSidePropsContext, PreviewData } from 'next';
import type { ParsedUrlQuery } from 'querystring';

import {
  getMirrorConfig,
  getServerStore,
  getUserInfo,
  initAuthData,
  MirrorExperiment,
  setI18nLocale,
  setTrackCommonData,
  setTrackSourceData,
} from '../state';
import { IAuthData } from '../types';
import { extractQueryParamsFromUrl, getAuthDataFromNextContext, PLATFROM_REG } from '../utils';

// 格式类似：Forwarded: for=192.0.2.43; proto=http; by=203.0.113.43
const getIpFromForwarded = (forwarded?: string) => {
  if (!forwarded) return null;

  // 使用正则表达式提取 for= 后面的 IP 地址
  const forwardedParts = forwarded.split(';');
  for (let part of forwardedParts) {
    part = part.trim();
    if (part.startsWith('for=')) {
      // 去掉 for= 前缀，取值
      let ip = part.substring(4).trim();

      // 去掉可能的引号
      if (ip.startsWith('"') && ip.endsWith('"')) {
        ip = ip.substring(1, ip.length - 1);
      }

      return ip;
    }
  }

  return null;
};

const getIpFromRequest = (req: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>['req']) => {
  if (req.socket.remoteAddress) return req.socket.remoteAddress;
  if (!req.headers) return '';
  const ipFromHeader =
    req.headers['x-real-ip'] ||
    req.headers['x-forwarded-for'] ||
    req.headers['x-client-ip'] ||
    req.headers['x-cluster-client-ip'] ||
    req.headers['true-client-ip'] ||
    req.headers['cf-connection-ip'] ||
    getIpFromForwarded(req.headers['forwarded']) ||
    '';
  if (Array.isArray(ipFromHeader)) return ipFromHeader[0] || '';
  return ipFromHeader;
};

const getUtmFromRequest = (req: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>['req']) => {
  const urlParams = extractQueryParamsFromUrl(req.url);
  const trackSourceData = {
    utm_source: urlParams?.get('utm_source'),
    utm_medium: urlParams?.get('utm_medium'),
    utm_campaign: urlParams?.get('utm_campaign'),
    edm_user_id: urlParams?.get('edm_user_id'),
  };
  return _.omitBy(trackSourceData, _.isNil);
};

const initMirrorConfig = async (
  serverStore: ReturnType<typeof getServerStore>,
  isBot: boolean,
  promises: Promise<any>[]
) => {
  // Skip mirror experiments for search engine bots
  if (isBot) {
    return;
  }

  // Initialize all mirror experiments
  const experiments = Object.values(MirrorExperiment) as MirrorExperiment[];

  const mirrorPromises = experiments.map((experiment) =>
    serverStore.dispatch(
      getMirrorConfig({
        params: { experiment },
      })
    )
  );

  promises.push(...mirrorPromises);
};

export async function NextServerSideInit(
  context: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>
) {
  const { req } = context;

  const serverStore = getServerStore();
  const authData = getAuthDataFromNextContext(req.headers);
  const isBot = PLATFROM_REG.IsSearchEngine.test(req.headers['user-agent'] || '');

  serverStore.dispatch(initAuthData(authData as unknown as IAuthData));
  serverStore.dispatch(setI18nLocale(context.locale || 'en'));
  serverStore.dispatch(setTrackCommonData({ ip: getIpFromRequest(context.req) }));
  serverStore.dispatch(setTrackSourceData(getUtmFromRequest(context.req)));

  const promises: any[] = [];
  // 如果登陆了，默认全局均请求（后面看要不要根据路由过滤）
  if (!authData.isAnonymous) {
    // promises.push(serverStore.dispatch(getChattedList()));
    promises.push(serverStore.dispatch(getUserInfo()));
  }

  initMirrorConfig(serverStore, isBot, promises);

  return {
    serverStore,
    promises,
  };
}
